import { cn } from '@costar-ui/cosmos'
import { getNormalizedScrollLeft, mergeRefs } from '@costar-ui/utils'
import React, { useCallback, useMemo, useRef } from 'react'
import type { Ref } from 'react'

import { useTableContext } from './table-context-provider'
import type { TableProps } from './types'

const Table = React.forwardRef<HTMLTableElement, TableProps>((props, ref) => {
  const { className, containerProps, ...rest } = props
  const {
    ref: containerRefProp,
    className: containerClassName,
    ...containerRest
  } = containerProps || {}

  const { fillContainer, direction = 'ltr', stickyColumns } = useTableContext()
  const _containerRef = useRef<HTMLDivElement>()
  const containerRef = useMemo(
    () => mergeRefs(containerRefProp, _containerRef) as Ref<HTMLDivElement>,
    [containerRefProp]
  )

  const [pinLeft, setPinLeft] = React.useState(false)
  const [pinRight, setPinRight] = React.useState(false)
  const handleOnScroll = useCallback(() => {
    const currentTarget = _containerRef.current
    if (currentTarget) {
      const scrollLeft = getNormalizedScrollLeft(currentTarget, direction)
      const { scrollWidth, clientWidth } = currentTarget

      // There is no space to scroll
      if (scrollWidth === clientWidth) {
        setPinLeft(false)
        setPinLeft(false)
        return
      }
      if (direction === 'rtl') {
        setPinLeft(-scrollLeft < scrollWidth - clientWidth)
        setPinRight(-scrollLeft > 0)
      } else {
        setPinLeft(scrollLeft > 0)
        setPinRight(scrollLeft < scrollWidth - clientWidth)
      }
    }
  }, [direction])

  return (
    <div
      ref={containerRef}
      className={cn(
        'csg-tw-table-content overflow-auto',
        {
          'w-full': fillContainer === 'fluid',
          'csg-tw-table-pin-left': pinLeft,
          'csg-tw-table-pin-right': pinRight
        },
        containerClassName
      )}
      onScroll={handleOnScroll}
      {...containerRest}
    >
      <table
        ref={ref}
        className={cn(
          'csg-tw-table border-spacing-0',
          {
            'table-fixed': stickyColumns,
            'relative w-full': fillContainer === 'fluid'
          },
          className
        )}
        {...rest}
      />
    </div>
  )
})
Table.displayName = 'Table'

export default Table
